import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type CardType,
  type NewsCardContent,
} from "./types";

interface NewsCardProps {
  format: CardFormat;
  content: NewsCardContent;
  cardType: Extract<CardType, "NEWS" | "ANNOUNCEMENT">;
}

const NewsCard = forwardRef<HTMLDivElement, NewsCardProps>(function NewsCard(
  { format, content, cardType },
  ref,
) {
  const isStory = format === "story";
  const style = content.style ?? {};
  const theme = resolveCardTheme(content.theme ?? "signature", {
    monoColor: content.monoColor,
  });
  const isEditorial = (content.theme ?? "signature") === "editorial";

  const eyebrowStyle: CSSProperties = {
    ...resolveTextStyle(
      style.eyebrow,
      {
        fontFamily: theme.fontEyebrow,
        fontSizePx: 22,
        color: theme.accentColor,
        letterSpacing: "0.5em",
        bold: true,
      },
      format,
    ),
    textTransform: "uppercase",
    marginTop: isStory ? 80 : 48,
  };

  const headlineDefault = isEditorial ? 124 : 156;
  const headlineStyle: CSSProperties = {
    ...resolveTextStyle(
      style.headline,
      {
        fontFamily: theme.fontHeadline,
        fontSizePx: headlineDefault,
        color: theme.headlineColor,
        letterSpacing: isEditorial ? "-0.01em" : "0.005em",
        bold: isEditorial ? true : false,
        italic: isEditorial,
      },
      format,
    ),
    lineHeight: isEditorial ? 1.0 : 0.9,
    marginTop: isStory ? 28 : 20,
  };

  const goldHighlightStyle: CSSProperties = {
    ...resolveTextStyle(
      style.goldHighlight,
      {
        fontFamily: theme.fontHeadline,
        fontSizePx: headlineDefault,
        color: theme.accentColor,
        bold: isEditorial ? true : false,
        italic: isEditorial,
      },
      format,
    ),
    display: "block",
    lineHeight: isEditorial ? 1.0 : 0.9,
    fontSize:
      (style.headline?.fontSizePx ?? style.goldHighlight?.fontSizePx ?? headlineDefault) *
      (isStory ? 1 : 0.78),
  };

  const bodyStyle: CSSProperties = {
    ...resolveTextStyle(
      style.body,
      {
        fontFamily: theme.fontBody,
        fontSizePx: 28,
        color: theme.subtleTextColor,
        bold: false,
      },
      format,
    ),
    lineHeight: 1.5,
    marginTop: isStory ? 36 : 28,
    maxWidth: isStory ? 900 : 920,
  };

  const ctaFontSize = isStory ? 26 : 22;
  const ctaStyle: CSSProperties = {
    ...resolveTextStyle(
      style.ctaText,
      {
        fontFamily: theme.fontBody,
        fontSizePx: 26,
        color: theme.ctaFg,
        bold: true,
      },
      format,
    ),
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    marginTop: isStory ? 48 : 36,
    padding: isStory ? "20px 36px" : "16px 30px",
    borderRadius: 9999,
    background: theme.ctaBg,
    fontSize: ctaFontSize,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    boxShadow: `0 20px 50px ${theme.ctaBg}40`,
    textAlign: undefined,
  };

  const imageMaxHeight = isStory ? 720 : 480;

  return (
    <FbkCardShell
      ref={ref}
      format={format}
      footerLeft={content.footerLeft || defaultFooterLeft(cardType)}
      footerRight={content.hashtags || "#WC2026 · #FBK"}
      header={content.header}
      background={style.background}
      customBaseGradient={theme.customBaseGradient}
      headerOnLight={theme.headerOnLight}
      suppressBrandOverlays={theme.suppressBrandOverlays}
      hideFooter={content.footer?.hide}
    >
      <ThemeOverlay theme={theme} format={format} />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div style={eyebrowStyle}>{content.eyebrow}</div>

        <div style={headlineStyle}>
          {content.headline}
          {content.goldHighlight && (
            <span style={goldHighlightStyle}>{content.goldHighlight}</span>
          )}
        </div>

        {content.body && <div style={bodyStyle}>{content.body}</div>}

        {content.imageUrl && (
          <div
            style={{
              marginTop: isStory ? 48 : 36,
              borderRadius: 24,
              overflow: "hidden",
              boxShadow: `0 0 0 4px ${theme.ringColor}80, 0 30px 60px rgba(0,0,0,0.45)`,
              maxHeight: imageMaxHeight,
            }}
          >
            <img
              src={content.imageUrl}
              alt=""
              crossOrigin={
                content.imageUrl.startsWith("data:") ? undefined : "anonymous"
              }
              style={{
                display: "block",
                width: "100%",
                maxHeight: imageMaxHeight,
                objectFit: "cover",
              }}
            />
          </div>
        )}

        {content.ctaText && <div style={ctaStyle}>{content.ctaText} →</div>}
      </div>
    </FbkCardShell>
  );
});

export default NewsCard;
